import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { TextField } from "@fleet/ui";
import { Loader2, Mail, Shield, Trash2, User as UserIcon, UserPlus } from "@fleet/icons";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createAccountPowerSyncSingle } from "@/lib/powersync/create-account-query";
import { getListOrganizationQuery, queryKeys } from "@listto/shared";
import { useOrganizationMembers } from "@/features/organizations/hooks/use-organization-members";
import { useI18n } from "@/providers/i18n-provider";

interface ShareListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listId: string;
  listName: string;
}

export function ShareListDialog(props: ShareListDialogProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const [email, setEmail] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);

  // lingui requires a plain `${identifier}` (not `${props.listName}`); pass it as
  // a function arg so there is no `const = props.x` for oxlint to autodestructure
  // (which would then trip solid/reactivity). Read inside JSX = tracked scope.
  const collaborateTitle = (listName: string) => t(msg`Collaborate on "${listName}"`);

  const orgLink = createAccountPowerSyncSingle<{ organization_id: string | null }>({
    queryKey: () => queryKeys.lists.organization(props.listId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getListOrganizationQuery(dbInstance, props.listId) : null;
    },
    enabled: () => Boolean(props.listId) && Boolean(drizzle()),
  });

  const { members, isLoading, inviteMember, removeMember } = useOrganizationMembers(
    () => orgLink.data?.organization_id ?? undefined,
  );

  const handleInvite = async (e: Event) => {
    e.preventDefault();
    const mail = email().trim();
    if (!mail || !orgLink.data?.organization_id) {
      return;
    }

    setIsSubmitting(true);
    try {
      await inviteMember(mail, "member");
      setEmail("");
      showToast(t(msg`Invitation sent`), "success");
    } catch (err) {
      console.error("Error inviting user:", err);
      showToast(t(msg`Error sending invitation`), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async (memberId: string) => {
    try {
      await removeMember(memberId);
    } catch (err) {
      console.error("Error removing member:", err);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md p-6">
      <DialogHeader size="md" border={false} class="mb-4 !p-0">
        <div class="flex items-center gap-3 text-primary">
          <UserPlus class="h-6 w-6" />
          <DialogTitle class="text-xl font-bold">{collaborateTitle(props.listName)}</DialogTitle>
        </div>
      </DialogHeader>

      <Show
        when={orgLink.data}
        fallback={
          <div class="py-10 text-center space-y-4">
            <Shield class="mx-auto h-12 w-12 text-muted-foreground opacity-20" />
            <p class="text-sm text-muted-foreground">
              {t(msg`This list is private. To collaborate, link this list to an organization.`)}
            </p>
            <Button variant="outline" class="w-full rounded-xl h-11 font-bold">
              {t(msg`Link to Organization`)}
            </Button>
          </div>
        }
      >
        <p class="text-sm text-muted-foreground mb-6">
          {t(msg`This list is part of an organization. Members listed below have access.`)}
        </p>

        <form onSubmit={(e) => void handleInvite(e)} class="flex gap-2 mb-8">
          <div class="relative flex-1">
            <Mail class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <TextField
              placeholder={t(msg`email@example.com`)}
              value={email()}
              onInput={(e) => setEmail(e.currentTarget.value)}
              class="pl-9 h-11 rounded-xl"
              disabled={isSubmitting()}
            />
          </div>
          <Button
            type="submit"
            disabled={isSubmitting() || !email().includes("@")}
            class="rounded-xl h-11 px-6 font-bold"
          >
            <Show when={isSubmitting()} fallback={t(msg`Invite`)}>
              <Loader2 class="h-4 w-4 animate-spin" />
            </Show>
          </Button>
        </form>

        <div class="space-y-4">
          <h4 class="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center justify-between opacity-50 px-1">
            {t(msg`Organization Members`)}
            <Show when={isLoading()}>
              <Loader2 class="h-3 w-3 animate-spin" />
            </Show>
          </h4>
          <div class="space-y-3 max-h-[300px] overflow-y-auto pr-2 scrollbar-thin">
            <For each={members()}>
              {(member) => (
                <div class="flex items-center justify-between group p-1">
                  <div class="flex items-center gap-3">
                    <div class="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary transition-transform group-hover:scale-105">
                      <UserIcon class="h-5 w-5" />
                    </div>
                    <div class="flex flex-col min-w-0">
                      <span class="text-sm font-bold truncate">
                        {member.user.name || member.user.email}
                      </span>
                      <span class="text-[10px] text-muted-foreground capitalize font-medium">
                        {member.role}
                      </span>
                    </div>
                  </div>
                  <Show when={member.role !== "owner"}>
                    <ConfirmDialog
                      title={t(msg`Remove member?`)}
                      description={t(
                        msg`Are you sure you want to remove this user from the shared list?`,
                      )}
                      confirmLabel={t(msg`Remove`)}
                      variant="destructive"
                      onConfirm={() => void handleRemove(member.id)}
                      renderTrigger={(open) => (
                        <Button
                          variant="ghost"
                          size="icon"
                          class="h-9 w-9 text-destructive opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"
                          onClick={open}
                        >
                          <Trash2 class="h-4 w-4" />
                        </Button>
                      )}
                    />
                  </Show>
                </div>
              )}
            </For>
          </div>
        </div>
      </Show>

      <div class="mt-8 flex justify-end">
        <Button
          variant="ghost"
          class="h-11 px-8 rounded-xl font-bold"
          onClick={() => {
            props.onOpenChange(false);
          }}
        >
          {t(msg`Close`)}
        </Button>
      </div>
    </Dialog>
  );
}
